package com.evoapps.evoshift.viewmodels

import com.evoapps.evoshift.domain.util.AppClock
import com.evoapps.evoshift.domain.model.withPlan
import com.evoapps.evoshift.domain.usecase.billing.ObservePlanActiveUseCase
import kotlinx.datetime.number
import kotlinx.datetime.toLocalDateTime
import kotlinx.datetime.TimeZone
import com.evoapps.evoshift.domain.model.currentOrNearest
import com.evoapps.evoshift.domain.model.defaultWorkplaceId
import com.evoapps.evoshift.ui.util.UiText
import com.evoapps.evoshift.ui.util.toUiText
import evoshift.composeapp.generated.resources.Res
import evoshift.composeapp.generated.resources.error_no_workplace_access
import evoshift.composeapp.generated.resources.error_session_expired
import evoshift.composeapp.generated.resources.error_user_not_found
import evoshift.composeapp.generated.resources.team_invite_already_registered
import evoshift.composeapp.generated.resources.team_last_owner
import androidx.lifecycle.ViewModel
import androidx.lifecycle.viewModelScope
import com.evoapps.evoshift.domain.model.Roster
import com.evoapps.evoshift.domain.model.Capability
import com.evoapps.evoshift.domain.model.UserPermission
import com.evoapps.evoshift.domain.model.capabilities
import com.evoapps.evoshift.domain.model.mayAssign
import com.evoapps.evoshift.domain.usecase.SendOnboardingInviteUseCase
import com.evoapps.evoshift.domain.usecase.auth.GetCurrentUserUseCase
import com.evoapps.evoshift.domain.usecase.roster.GetRosterSummaryUseCase
import com.evoapps.evoshift.domain.usecase.team.GetTeamStatusUseCase
import com.evoapps.evoshift.domain.usecase.team.TeamMemberStatus
import com.evoapps.evoshift.domain.usecase.user.TerminateEngagementUseCase
import com.evoapps.evoshift.session.WorkplaceSessionManager
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.StateFlow
import kotlinx.coroutines.flow.asStateFlow
import kotlinx.coroutines.flow.catch
import kotlinx.coroutines.flow.collectLatest
import kotlinx.coroutines.flow.distinctUntilChanged
import kotlinx.coroutines.flow.first
import kotlinx.coroutines.flow.map
import kotlinx.coroutines.flow.onEach
import kotlinx.coroutines.flow.update
import kotlinx.coroutines.launch
import com.evoapps.evoshift.domain.model.StaffReadiness
import com.evoapps.evoshift.domain.model.ShiftLeadRule
import com.evoapps.evoshift.domain.usecase.GetWorkplaceUseCase
import com.evoapps.evoshift.domain.usecase.staff.GetTeamReadinessUseCase

data class TeamUiState(
    val isLoading: Boolean = false,
    val employees: List<TeamMemberStatus> = emptyList(), // Updated to TeamMemberStatus
    val inviteError: UiText? = null,
    val inviteSuccess: Boolean = false,
    val userPermission: UserPermission? = null,
    /** What the person may do at this workplace: adding and opening people needs MANAGE_TEAM. */
    val capabilities: Set<Capability> = emptySet(),
    val currentWorkplaceId: String? = null,
    val activeRosters: List<Roster> = emptyList(),
    val availableWorkplaceIds: Map<String, String> = emptyMap(),
    /** SH-47: each person's medical exam / OHS statuses (managers), for the list's warnings. */
    val readinessByUserId: Map<String, StaffReadiness> = emptyMap(),
    /** SH-104: who leads shifts here (the workplace's lead positions and skills), for the list's stars. */
    val leadRule: ShiftLeadRule = ShiftLeadRule(),
    val error: UiText? = null,
    /** SH-168: the team could not be read (nobody shown): Try again instead of an empty team. */
    val loadError: UiText? = null,
    /** SH-267: "Guests this month": people of the group's other workplaces with shifts here this month (managers). */
    val guests: List<TeamGuest> = emptyList()
)

/** SH-267: a person borrowed from another workplace of the group, as the Team screen lists them. */
data class TeamGuest(val userId: String, val displayName: String, val photoUrl: String?, val homeWorkplaceName: String?)

sealed interface TeamEvent {
    data class LoadTeam(val workplaceId: String) : TeamEvent
    /** The Team screen is shown again (e.g. back from adding or editing someone): re-read the people quietly. */
    data object ScreenShown : TeamEvent
    data class TerminateEmployee(val userId: String, val workplaceId: String) : TeamEvent
    data object ResetStatus : TeamEvent
    data class SendInviteToExistingMember(val userId: String, val email: String) : TeamEvent
    data class SelectWorkplace(val workplaceId: String) : TeamEvent
    /** SH-168: Try again after the team failed to load. */
    data object Retry : TeamEvent
}

class TeamManagementViewModel(
    private val getTeamStatusUseCase: GetTeamStatusUseCase, // Injected
    private val sendInviteUseCase: SendOnboardingInviteUseCase,
    private val terminateUseCase: TerminateEngagementUseCase,
    private val getCurrentUserUseCase: GetCurrentUserUseCase,
    private val getRosterSummaryUseCase: GetRosterSummaryUseCase,
    private val sessionManager: WorkplaceSessionManager,
    private val observePlanActive: ObservePlanActiveUseCase? = null,
    private val getTeamReadiness: GetTeamReadinessUseCase? = null,
    private val getWorkplace: GetWorkplaceUseCase? = null,
    /** SH-267: the month's guests (borrowed people) and their public profiles; without them none are listed. */
    private val borrowing: com.evoapps.evoshift.domain.repository.BorrowingRepository? = null,
    private val userRepository: com.evoapps.evoshift.domain.repository.UserRepository? = null,
    private val thisMonth: () -> String = {
        AppClock.now().toLocalDateTime(TimeZone.currentSystemDefault()).date.toString().take(7)
    }
) : ViewModel() {

    private val _uiState = MutableStateFlow(TeamUiState())
    val uiState: StateFlow<TeamUiState> = _uiState.asStateFlow()
    private val rostersJob = JobSlot()
    private val employeesJob = JobSlot()
    private var shownOnce = false

    init {
        observeSession()
        loadInitialData()
    }

    private fun observeSession() {
        viewModelScope.launch {
            sessionManager.currentWorkplaceId.collectLatest { workplaceId ->
                if (workplaceId != null) {
                    refreshWorkplaceData(workplaceId)
                }
            }
        }
    }

    private fun loadInitialData() {
        viewModelScope.launch {
            // The spinner is the workplace load's (refreshWorkplaceData): setting it here, after
            // that load finished, left it spinning over a team that is not shown to this role.
            val user = getCurrentUserUseCase().first()
            if (user == null) {
                _uiState.update { it.copy(isLoading = false, error = UiText.Resource(Res.string.error_user_not_found)) }
                return@launch
            }

            val workplaceIds = user.engagements.mapValues {
                it.value.cachedWorkplaceName .orEmpty()
            }

            _uiState.update { it.copy(availableWorkplaceIds = workplaceIds) }

            if (sessionManager.currentWorkplaceId.value == null) {
                val firstAvailableId = user.defaultWorkplaceId()
                if (firstAvailableId != null) {
                    sessionManager.setWorkplaceId(firstAvailableId)
                } else {
                    _uiState.update { it.copy(isLoading = false) }
                }
            }
        }
    }

    private suspend fun refreshWorkplaceData(workplaceId: String) {
        // Another workplace's team never shows under this one's name: its list goes, the spinner shows.
        _uiState.update {
            if (it.currentWorkplaceId == workplaceId) it
            else it.copy(currentWorkplaceId = workplaceId, employees = emptyList(), readinessByUserId = emptyMap(), isLoading = true)
        }

        val user = getCurrentUserUseCase().first() ?: return
        val engagement = user.engagements[workplaceId]

        if (engagement != null) {
            internalSwitchContext(workplaceId, engagement.permission, engagement.capabilities.withPlan(observePlanActive.activeNow(workplaceId)))
            // The team is the managers' (an ended engagement may read nothing of the workplace).
            if (Capability.MANAGE_TEAM !in engagement.capabilities) {
                _uiState.update { it.copy(isLoading = false, employees = emptyList()) }
                return
            }
            loadRosters(workplaceId) // Load rosters first to get the latest/current roster ID
            loadReadiness(workplaceId)
            loadLeadRule(workplaceId)
            loadGuests(workplaceId)
        } else {
            _uiState.update { it.copy(isLoading = false, error = UiText.Resource(Res.string.error_no_workplace_access)) }
        }
    }

    /**
     * SH-267: who is planned here this month as borrowed from another workplace of the group, by
     * name, with where they come from (their public profile names their workplaces). A failed read
     * lists nobody.
     */
    private fun loadGuests(workplaceId: String) {
        val repository = borrowing ?: return
        viewModelScope.launch {
            val guests = repository.guests(workplaceId, thisMonth()).getOrNull().orEmpty()
            val listed = guests.mapNotNull { guest ->
                val profile = userRepository?.getUserProfile(guest.userId)?.getOrNull()
                val home = guest.homeWorkplaceId?.let { profile?.engagements?.get(it)?.cachedWorkplaceName }
                TeamGuest(guest.userId, profile?.displayName ?: return@mapNotNull null, profile?.photoUrl, home?.takeIf { it.isNotBlank() })
            }.sortedBy { it.displayName.lowercase() }
            _uiState.update { if (it.currentWorkplaceId == workplaceId) it.copy(guests = listed) else it }
        }
    }

    /** SH-104: the workplace's lead positions and skills (the owner and managers lead anyway). */
    private fun loadLeadRule(workplaceId: String) {
        val getWorkplace = getWorkplace ?: return
        viewModelScope.launch {
            val rule = getWorkplace(workplaceId).getOrNull()?.let { ShiftLeadRule.of(it) } ?: return@launch
            _uiState.update { if (it.currentWorkplaceId == workplaceId) it.copy(leadRule = rule) else it }
        }
    }

    fun onEvent(event: TeamEvent) {
        when (event) {
            is TeamEvent.LoadTeam -> loadEmployees(event.workplaceId)
            TeamEvent.ScreenShown -> {
                // The first showing is the initial load; later ones pick up people added or renamed
                // in the employee form (the team status is read once, not observed).
                if (shownOnce) _uiState.value.currentWorkplaceId?.let {
                    loadEmployees(it, quiet = true)
                    loadReadiness(it)
                }
                shownOnce = true
            }
            is TeamEvent.SelectWorkplace -> sessionManager.setWorkplaceId(event.workplaceId)
            TeamEvent.Retry -> _uiState.value.currentWorkplaceId?.let { workplaceId ->
                _uiState.update { it.copy(loadError = null, isLoading = it.employees.isEmpty()) }
                loadRosters(workplaceId)
                loadReadiness(workplaceId)
            }
            is TeamEvent.SendInviteToExistingMember -> sendInvite(event.userId, event.email)
            is TeamEvent.TerminateEmployee -> terminateEmployee(event.userId, event.workplaceId)
            is TeamEvent.ResetStatus -> _uiState.update {
                it.copy(inviteError = null, inviteSuccess = false, error = null)
            }
        }
    }

    private fun sendInvite(userId: String, email: String) {
        if (!_uiState.value.capabilities.shows(Gate.TEAM_EDITING)) return
        val workplaceId = _uiState.value.currentWorkplaceId ?: return
        val workplaceName = _uiState.value.availableWorkplaceIds[workplaceId].orEmpty()

        val targetUser = _uiState.value.employees.find { it.user.uid == userId }?.user
        if (targetUser != null && !targetUser.isShadow) {
            _uiState.update { it.copy(inviteError = UiText.Resource(Res.string.team_invite_already_registered)) }
            return
        }

        // Shown as sent at once; a failure takes it back and says why.
        val before = _uiState.value.inviteSuccess
        _uiState.update { it.copy(inviteSuccess = true, inviteError = null) }
        viewModelScope.launch {
            val manager = getCurrentUserUseCase().first()

            if (manager == null) {
                _uiState.update { it.copy(inviteSuccess = before, error = UiText.Resource(Res.string.error_session_expired)) }
                return@launch
            }

            sendInviteUseCase(
                email = email,
                workplaceName = workplaceName,
                workplaceId = workplaceId,
                invitedByManagerName = manager.displayName,
                invitedByManagerId = manager.uid,
                targetShadowUserId = userId
            ).onFailure { e ->
                _uiState.update { it.copy(inviteSuccess = before, inviteError = e.toUiText()) }
            }
        }
    }

    private fun internalSwitchContext(workplaceId: String, permission: UserPermission, capabilities: Set<Capability>) {
        _uiState.update {
            it.copy(
                userPermission = permission,
                capabilities = capabilities,
                activeRosters = emptyList()
            )
        }
    }

    private fun loadRosters(workplaceId: String) {
        rostersJob.launch(viewModelScope) {
            getRosterSummaryUseCase(workplaceId)
                // SH-168: a failed listener: the team still loads (it needs no roster); see loadEmployees.
                .catch { emit(emptyList()) }
                .map { rosters -> rosters.sortedByDescending { r -> r.year * 100 + r.month } }
                .onEach { sortedRosters -> _uiState.update { it.copy(activeRosters = sortedRosters) } }
                // Once rosters are loaded, we can deduce the "current" roster to fetch shifts from;
                // a roster update that keeps the same current roster does not reload the team.
                .map { sortedRosters -> currentRosterOf(sortedRosters)?.id }
                .distinctUntilChanged()
                // Always try to load employees, even if no roster exists (status will be empty)
                .collect { currentRosterId -> loadEmployees(workplaceId, currentRosterId) }
        }
    }

    /** The spinner shows only while the list is empty; a [quiet] re-read after a save never shows it. */
    private fun loadEmployees(workplaceId: String, rosterId: String? = null, quiet: Boolean = false) {
        // Use rosterId if provided, otherwise fallback to existing state roster (which might be null)
        val finalRosterId = rosterId ?: currentRosterOf(_uiState.value.activeRosters)?.id
        
        employeesJob.launch(viewModelScope) {
            if (!quiet) _uiState.update { it.copy(isLoading = it.employees.isEmpty()) }

            getTeamStatusUseCase(workplaceId, finalRosterId)
                // SH-168: nobody on screen yet: a load error (Try again); over the team shown, a snackbar.
                .catch { e ->
                    _uiState.update {
                        if (it.employees.isEmpty()) it.copy(isLoading = false, loadError = e.toUiText())
                        else it.copy(isLoading = false, error = e.toUiText())
                    }
                }
                .collect { statusList ->
                    _uiState.update { it.copy(isLoading = false, loadError = null, employees = statusList) }
                }
        }
    }

    private fun terminateEmployee(userId: String, workplaceId: String) {
        val target = _uiState.value.employees.find { it.user.uid == userId }?.user?.engagements?.get(workplaceId)?.permission
        val me = _uiState.value.userPermission
        // Nobody removes someone above their own level (the rules refuse the write).
        if (!_uiState.value.capabilities.shows(Gate.TEAM_EDITING) || me == null || (target != null && !me.mayAssign(target))) return
        viewModelScope.launch {
            val ownersCount = _uiState.value.employees.count {
                it.user.engagements[workplaceId]?.permission == UserPermission.OWNER &&
                        it.user.engagements[workplaceId]?.isActive == true
            }
            val userToTerminate = _uiState.value.employees.find { it.user.uid == userId }?.user
            val isOwner = userToTerminate?.engagements?.get(workplaceId)?.permission == UserPermission.OWNER

            if (isOwner && ownersCount <= 1) {
                _uiState.update { it.copy(error = UiText.Resource(Res.string.team_last_owner)) }
                return@launch
            }

            // Gone from the list at once (the team lists active engagements only); a failure puts them back.
            val before = _uiState.value.employees
            _uiState.update { state -> state.copy(employees = state.employees.filterNot { it.user.uid == userId }) }

            terminateUseCase(userId, workplaceId).fold(
                // The team status reads the people once (only shifts are live): re-read quietly.
                onSuccess = { loadEmployees(workplaceId, quiet = true) },
                onFailure = { e ->
                    _uiState.update { it.copy(employees = before, error = e.toUiText()) }
                }
            )
        }
    }

    /** SH-47: the statuses (not the dates) of everyone here, for the list's expiry warnings. */
    private fun loadReadiness(workplaceId: String) {
        val use = getTeamReadiness ?: return
        if (!_uiState.value.capabilities.shows(Gate.STAFF_STATUS)) return
        viewModelScope.launch {
            use(workplaceId).onSuccess { found ->
                if (_uiState.value.currentWorkplaceId == workplaceId) _uiState.update { it.copy(readinessByUserId = found) }
            }
        }
    }

    /** The month the team's status is about: this month, else the nearest (never a far-future draft). */
    private fun currentRosterOf(rosters: List<Roster>): Roster? {
        val today = AppClock.now().toLocalDateTime(TimeZone.currentSystemDefault()).date
        return rosters.currentOrNearest(today.year, today.month.number)
    }
}

